import React, { useState, useRef } from 'react';
import taskService from '../../services/task.service';
import { cn } from '../../lib/cn';

interface TaskAttachmentUploadProps {
  taskId?: string;
  onUploadComplete?: () => void;
  maxFileSize?: number; // in MB
  allowedExtensions?: string[];
}

interface FileWithProgress {
  file: File;
  progress: number;
  error?: string;
}

const formatFileSize = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  return mb < 1 ? `${(bytes / 1024).toFixed(2)}KB` : `${mb.toFixed(2)}MB`;
};

const TaskAttachmentUpload: React.FC<TaskAttachmentUploadProps> = ({
  taskId,
  onUploadComplete,
  maxFileSize = 10,
  allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'png', 'jpg', 'jpeg', 'gif', 'zip'],
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileWithProgress[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const validFiles: FileWithProgress[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      // Check file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxFileSize) {
        errors.push(`${file.name} exceeds maximum size of ${maxFileSize}MB`);
        return;
      }

      // Check file extension
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!extension || !allowedExtensions.includes(extension)) {
        errors.push(`${file.name} has an invalid file type`);
        return;
      }

      validFiles.push({ file, progress: 0 });
    });

    if (errors.length > 0) {
      setUploadError(errors.join(', '));
    } else {
      setUploadError(null);
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!taskId || selectedFiles.length === 0) return;

    setUploading(true);
    setUploadError(null);

    try {
      // Upload files sequentially
      for (let i = 0; i < selectedFiles.length; i++) {
        const fileWithProgress = selectedFiles[i];

        try {
          // Update progress to show upload started
          setSelectedFiles((prev) =>
            prev.map((f, idx) =>
              idx === i ? { ...f, progress: 50 } : f
            )
          );

          await taskService.uploadTaskAttachment(taskId, fileWithProgress.file);

          // Update progress to complete
          setSelectedFiles((prev) =>
            prev.map((f, idx) =>
              idx === i ? { ...f, progress: 100 } : f
            )
          );
        } catch (error) {
          // Mark file as failed
          setSelectedFiles((prev) =>
            prev.map((f, idx) =>
              idx === i ? { ...f, progress: 0, error: 'Upload failed' } : f
            )
          );
        }
      }

      // Remove successfully uploaded files
      setSelectedFiles((prev) => prev.filter((f) => f.progress !== 100));

      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      setUploadError('Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label
        className={cn(
          'block cursor-pointer border border-ink px-3 py-4 text-center hover:bg-paper-raised',
          'has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-accent',
          uploading && 'pointer-events-none opacity-60',
        )}
      >
        <span className="cap-label">Select files to upload</span>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          disabled={uploading}
          onChange={handleFileSelect}
          accept={allowedExtensions.map((ext) => `.${ext}`).join(',')}
          data-testid="attachment-file-input"
          className="sr-only"
        />
      </label>

      {uploadError && (
        <div
          role="alert"
          className="mt-3 flex items-start justify-between gap-2 border border-sev-critical px-3 py-2 text-scale-1 text-sev-critical"
        >
          <span>{uploadError}</span>
          <button
            type="button"
            aria-label="Dismiss error"
            onClick={() => setUploadError(null)}
            className="cursor-pointer hover:opacity-70"
          >
            &#x2715;
          </button>
        </div>
      )}

      {selectedFiles.length > 0 && (
        <div className="mt-3">
          <div className="cap-label">Selected Files ({selectedFiles.length})</div>
          {selectedFiles.map((fileWithProgress, index) => (
            <div key={index} className="border-b border-rule-faint py-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="min-w-0 flex-1 truncate text-scale-1 text-ink" title={fileWithProgress.file.name}>
                  {fileWithProgress.file.name}{' '}
                  <span className="font-mono text-scale-0 text-ink-muted">
                    ({formatFileSize(fileWithProgress.file.size)})
                  </span>
                </span>
                {!uploading && (
                  <button
                    type="button"
                    aria-label={`Remove ${fileWithProgress.file.name}`}
                    onClick={() => handleRemoveFile(index)}
                    className="cap-label shrink-0 cursor-pointer hover:!text-ink"
                  >
                    Remove
                  </button>
                )}
              </div>
              {fileWithProgress.progress > 0 && fileWithProgress.progress < 100 && (
                <div className="mt-1 h-1 w-full bg-rule-faint">
                  <div className="h-1 bg-accent" style={{ width: `${fileWithProgress.progress}%` }} />
                </div>
              )}
              {fileWithProgress.error && (
                <p className="mt-0.5 text-scale-0 text-sev-critical">{fileWithProgress.error}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="mt-2 text-scale-0 text-ink-muted">
        Allowed: {allowedExtensions.join(', ').toUpperCase()}. Max {maxFileSize}MB per file.
      </p>

      {selectedFiles.length > 0 && (
        <button
          type="button"
          onClick={handleUpload}
          disabled={!taskId || uploading}
          className="mt-3 w-full cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:cursor-default disabled:opacity-60"
        >
          {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''}`}
        </button>
      )}
    </div>
  );
};

export default TaskAttachmentUpload;
